export interface SplitBreakdown {
  primary: number;
  secondary: number;
  total: number;
  primaryShare: number;
  secondaryShare: number;
  primaryPercentage: number;
  secondaryPercentage: number;
}

const normalizeAmount = (value?: number | null) => {
  const amount = Number(value) || 0;
  return Number.isFinite(amount) ? Math.max(amount, 0) : 0;
};

/**
 * Splits two non-negative values into complementary shares and percentages.
 *
 * Shared by every two-sided split card (call/put contracts, bearish/bullish premium)
 * so the bar widths and the printed percentages are always one computation.
 *
 * A side carrying any weight at all is floored at 1% (and the other capped at 99%):
 * a rounded 0% beside a non-zero amount reads as "none" and is indistinguishable
 * from the genuine empty state. The percentages stay complementary either way, so
 * the two printed numbers always sum to 100.
 */
export const calculateSplit = (primaryValue = 0, secondaryValue = 0): SplitBreakdown => {
  const primary = normalizeAmount(primaryValue);
  const secondary = normalizeAmount(secondaryValue);
  const total = primary + secondary;
  const primaryShare = total === 0 ? 0 : primary / total;
  const secondaryShare = total === 0 ? 0 : secondary / total;
  const primaryPercentage =
    total === 0
      ? 0
      : Math.min(
          Math.max(Math.round(primaryShare * 100), primary > 0 ? 1 : 0),
          secondary > 0 ? 99 : 100,
        );

  return {
    primary,
    secondary,
    total,
    primaryShare,
    secondaryShare,
    primaryPercentage,
    secondaryPercentage: total === 0 ? 0 : 100 - primaryPercentage,
  };
};
